import { Component, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';

import { UrbanPlannerRegistrationStateService } from '../../../app/shared/services/registration/urban-planner-registration-state/urban-planner-registration-state.service';
import { EstateAgentRegistrationStateService } from '../../../app/shared/services/registration/estate-agent-registration-state/estate-agent-registration-state.service';
import { NotificationService } from '../../../app/shared/services/notification-service/notification.service';
import { LookupService, UserManagementService } from '../../../app/services/property-matrixV2/services';
import { RoleDto, UserDto, UserRoleDto } from '../../../app/services/property-matrixV2/models';
import { GlobalConstants, UserRoles } from '../../../app/common/global-constants';
import { GlobalMethods } from '../../../app/common/global-methods';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss', '../../../css/2-modules/_forms.scss'],
})

export class RegisterComponent {
  @Output() pageNumberEvent = new EventEmitter<number>();

  loading: boolean = false;
  isPrincipal: boolean = false;
  isRsaResident: boolean = true;
  confirmConsent: boolean = false;
  passwordHidden: boolean = true;
  confirmPasswordHidden: boolean = true;
  userRoleDtoItem: UserRoleDto = {};
  inviteId: string | null = "";
  userType: string | null = null;
  currentUserType: string | null = null;
  rolesCanNotBeEdited: boolean = false;
  roleList: RoleDto[] = [];
  selectedRole: RoleDto = {};
  emailPattern: string = GlobalConstants.emailPattern;
  passwordPattern: string = GlobalConstants.passwordPattern;
  agentLinkSentParameterName: string | null = null;
  agentLinkSentParameterSurname: string | null = null;
  agentLinkSentParameterEmail: string | null = null;
  titleText: string | null = null;

  userForm: UserDto = {
    confirmEmail: '',
    confirmMobile: '',
    email: '',
    idNumber: '',
    isSouthAfricanResident: this.isRsaResident,
    landLine: '',
    mobile: '',
    name: '',
    passportNumber: '',
    surname: '',
    userName: '',
    password: '',
    passwordConfirm: '',
    userRoles: []
  };

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _keycloak: KeycloakService,
    private _authService: UserManagementService,
    private _notificationService: NotificationService,
    private _estateAgentRegistrationStateService: EstateAgentRegistrationStateService,
    private _urbanPlannerRegistrationStateService: UrbanPlannerRegistrationStateService,
    private _lookupService: LookupService,
  ) { }

  ngOnInit(): void {
    this.loadUserRoles();
    this._route.queryParams.subscribe(params => {
      this.userType = params['userType'];
      if (this.userType === 'estate-agent') {
        this.isPrincipal = params['isPrincipal'] === 'true';
        this.inviteId = params['inviteId'];
        if (params['agentName']) {
          this.agentLinkSentParameterName = params['agentName'];
          this.userForm.name = this.agentLinkSentParameterName;
        }
        if (params['agentSurname']) {
          this.agentLinkSentParameterSurname = params['agentSurname'];
          this.userForm.surname = this.agentLinkSentParameterSurname;
        }
        if (params['agentEmail']) {
          this.agentLinkSentParameterEmail = params['agentEmail'];
          this.userForm.email = this.agentLinkSentParameterEmail;
          this.userForm.confirmEmail = this.agentLinkSentParameterEmail;
          this.userForm.userName = this.agentLinkSentParameterEmail;
        }
      }
    });
    this.setTitleText();

    GlobalMethods.scrollToTop();
  }

  autoFillUsername(email: string) {
    if (!email) return;
    this.userForm.userName = email;
  }

  onResidencyChange(event: any): void {
    this.isRsaResident = event.value;
    this.userForm.isSouthAfricanResident = this.isRsaResident
  }

  public register(): void {
    switch (this.userType) {
      case 'website-user':
        this.currentUserType = "Website User";
        this.registerUser(this._keycloak.login.bind(this._keycloak));
        break;
      case 'estate-agent':
        if (this.isPrincipal == false && this.agentLinkSentParameterName != "") {
          this.currentUserType = "Network Agent";
          this.registerUser(this.handleEstateAgentRegistration.bind(this));
          break;
        } else {
          this.currentUserType = "Principal Agent";
          this.registerUser(this.handleEstateAgentRegistration.bind(this));
          break;
        }
      case 'urban-planner':
        this.currentUserType = "Network Planner";
        this.registerUser(this.handleUrbanPlannerRegistration.bind(this));
        break;
      default:
        this._notificationService.showErrorMessage('Error', 'No user type selected.');
    }
  }

  setTitleText(): void {
    switch (this.userType) {
      case 'estate-agent':
        this.currentUserType = "Network Agent";
        break;
      case 'urban-planner':
        this.currentUserType = "Network Planner";
        break;
      case 'website-user':
        this.currentUserType = "Website User";
      default:
        break;
    }
    this.titleText = "Registration For Log-In: " + (this.currentUserType ?? "");
  }

  private async registerUser(successCallback: (result: any) => void): Promise<void> {
    this.loading = true;
    let userRegistrationForm: UserDto = { ...this.userForm };
    if (this.currentUserType != '') {
      userRegistrationForm.userRoles = [];

      const currentRoleForUser = this.roleList.find(r => r.name?.toLowerCase() === this.currentUserType?.toLowerCase());
      if (currentRoleForUser) {
        userRegistrationForm.userRoles.push({
          roleid: currentRoleForUser.id,
          userid: null,
          roleName: currentRoleForUser.name,
        });
      }
    } else {
      userRegistrationForm.userRoles?.push(this.selectedRole as RoleDto);
    }

    userRegistrationForm.isPrincipal = this.isPrincipal ?? false;
    userRegistrationForm.userType = this.userType ?? "";
    if (this.inviteId) {
      userRegistrationForm.inviteId = this.inviteId;
    }
    try {
      this._authService.apiV1UserManagementAddUserPost({
        body: userRegistrationForm
      }).subscribe({
        next: (result: any) => {
          if (result.success) {
            successCallback(result);
          } else {
            this.handleRegistrationError(result.errorMessage);
          }
          this.loading = false;
        },
        error: (_error) => {
          this._notificationService.showErrorMessage('Error', 'Something went wrong. Please try again.');
          this.loading = false;
        }
      });
    }
    catch (e) {
      this._notificationService.showErrorMessage('Error', 'Something went wrong. Please try again.');
      this.loading = false;
    }
  }

  private handleRegistrationError(errorMessage: string): void {
    if (errorMessage.includes('Duplicate entry')) {
      this._notificationService.showErrorMessage('Error', 'Duplicate entry. The user already exists.');
    } else {
      this._notificationService.showErrorMessage('Error', 'Something went wrong. Please try again.');
    }
  }

  private handleEstateAgentRegistration(result: any): void {
    if (this.isPrincipal) {
      this._estateAgentRegistrationStateService.setIsPrincipal(true);
      this._estateAgentRegistrationStateService.setPrincipalUserId(result.userId);
    } else {
      this._estateAgentRegistrationStateService.setIsPrincipal(false);
      this._estateAgentRegistrationStateService.setPrincipalUserId(this.inviteId);
    }
    this._estateAgentRegistrationStateService.setUserId(result.userId);
    this.proceed(result.userId);
  }

  private handleUrbanPlannerRegistration(result: any): void {
    this._urbanPlannerRegistrationStateService.setUserId(result.userId);
    this.proceed(result.userId);
  }

  private proceed(userId: any): void {

    this._route.queryParams.subscribe(params => {
      const navigationExtras = {
        queryParams: { ...params, 'userID': userId }
      };
      this._router.navigate(['introduction-step'], navigationExtras);
    });
  }

  private async loadUserRoles(): Promise<void> {
    try {
      await this._lookupService.apiV1LookupGetUserRolesGet().subscribe({
        next: (rolesList: RoleDto[]) => {
          this.roleList = [];
          this.roleList = rolesList;
          this.setUserRoleByDefault();
        },
        error: (_error: any) => {
          this._notificationService.showErrorMessage('Error', 'Could not load roles for users.');
        }
      });
    }
    catch (e) {
      this._notificationService.showErrorMessage('Error', 'Could not load roles for users.');
    }
  }

  onLeaveSetUserName(): void {
    this.userForm.userName = this.userForm.email;
  }

  private setUserRoleByDefault(): void {
    if (this.userType == 'estate-agent') {
      if (this.isPrincipal == false && this.agentLinkSentParameterName != "") {
        this.selectedRole = this.roleList.find(b => b.name?.toLowerCase() == "network agent");
        this.rolesCanNotBeEdited = true;
      } else {
        this.selectedRole = this.roleList.find(b => b.name?.toLowerCase() == "principal agent");
        this.rolesCanNotBeEdited = true;
      }
    } else if (this.userType == 'urban-planner') {
      this.selectedRole = this.roleList.find(b => b.name?.toLowerCase() == "urban planner");
      this.rolesCanNotBeEdited = true;
    } else if (this.userType == 'website-user') {
      this.selectedRole = this.roleList.find(b => b.name?.toLowerCase() == "website user");
      this.rolesCanNotBeEdited = true;
    }
  }

  public compareRoles(role1: RoleDto, role2: RoleDto) {
    return role1 && role2 ? role1.name === role2.name : role1 === role2;
  }

  passwordShow() {
    this.passwordHidden = !this.passwordHidden;
  }

  confirmPasswordShow() {
    this.confirmPasswordHidden = !this.confirmPasswordHidden;
  }
}
